import React from "react";
import { DateTimeField, StatusField } from "./components/CustomColumns";
import { OfferPriceBadges } from "./components/OfferPriceBadges";
import { OfferingNameChips } from "features/PaymentLinks/modules/ExistingPaymentLinks/components/OfferingNameChip/OfferingNameChip";
import {
  OFFER_TARGET_TYPE,
  OFFER_TARGET_TYPE_OPTIONS,
} from "ui/modules/LimitedOfferModalContent/constants";
import {
  directionLabel,
  isSurgeRecord,
} from "ui/modules/LimitedOfferModalContent/utils/offerContract";
import {
  formatRevenue,
  SHOW_TOTAL_VISITORS,
} from "features/Common/utils/common.utils";

export const columnConfig = (classes) => [
  {
    field: "title",
    headerName: "Offering name",
    showInCard: true,
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
    ellipsis: true,
    maxCharCount: 30,
  },
  {
    field: "target_type",
    headerName: "Created for",
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
    ellipsis: true,
    maxCharCount: 30,
    valueFormatter: (record) =>
      OFFER_TARGET_TYPE_OPTIONS.find(
        (option) => option.value === record.target_type
      )?.label || "N/A",
  },
  {
    field: "listing_name",
    headerName: "Page(s)",
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
    ellipsis: true,
    maxCharCount: 30,
    valueFormatter: formatOfferingName,
  },
  {
    field: "price",
    headerName: "Offer Price",
    showInCard: true,
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
    valueFormatter: (record) => <OfferPriceBadges record={record} />,
  },
  {
    field: "offer_direction",
    headerName: "Type",
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
    valueFormatter: (record) => directionLabel(record),
  },
  {
    field: "trigger_label",
    headerName: "Trigger",
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
    ellipsis: true,
    maxCharCount: 30,
    valueFormatter: (record) =>
      record?.trigger_label || record?.trigger_summary || "N/A",
  },
  ...(SHOW_TOTAL_VISITORS
    ? [
        {
          field: "total_visitors",
          headerName: "No. of visitors",
          emptyField: "N/A",
          noWrap: true,
          sortable: false,
        },
      ]
    : []),
  {
    field: "total_leads",
    headerName: "No. of leads",
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
  },
  {
    field: "total_bookings",
    headerName: "No. of bookings",
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
  },
  {
    field: "total_revenue",
    headerName: "Total revenue",
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
    valueFormatter: (record) =>
      formatRevenue(record?.total_revenue, null, 2),
  },
  {
    field: "total_discount",
    headerName: "Total discount given",
    showInCard: true,
    emptyField: "N/A",
    sortable: false,
    noWrap: true,
    valueFormatter: (record) => formatRevenue(record?.total_discount),
  },
  {
    field: "total_surge_added",
    headerName: "Total surge value added",
    emptyField: "N/A",
    sortable: false,
    noWrap: true,
    valueFormatter: (record) =>
      isSurgeRecord(record)
        ? formatRevenue(record?.total_surge_added, null, 2)
        : "N/A",
  },
  {
    field: "times_applied",
    headerName: "Times Applied",
    emptyField: "N/A",
    sortable: false,
    noWrap: true,
    valueFormatter: (record) => record?.times_applied ?? "N/A",
  },
  {
    field: "end_datetime",
    headerName: "End date",
    emptyField: "N/A",
    noWrap: true,
    sortable: false,
    valueFormatter: (record) => (
      <DateTimeField record={record} source="end_datetime" />
    ),
    columnClassName: classes.endDate,
  },
  {
    field: "status",
    headerName: "Status",
    valueFormatter: (record) => <StatusField record={record} />,
    align: "left",
    hidden: true,
  },
];

export const filterConfig = [
  {
    source: "title",
    placeholder: "Offer Name",
    type: "input",
  },
  {
    source: "listing__title",
    placeholder: "Offering Name",
    type: "input",
  },
  {
    source: "status",
    placeholder: "All Status",
    type: "dropdown",
    options: [
      { label: "Active", value: 1 },
      { label: "Scheduled", value: 0 },
      { label: "Expired", value: 2 },
    ],
  },
  {
    source: "direction",
    placeholder: "All Offers",
    type: "dropdown",
    options: [
      { label: "Early Bird", value: 1 },
      { label: "Surge", value: 2 },
    ],
  },
];

export const tableChipsFormatter = (filter, value) => {
  switch (filter) {
    case "title__icontains":
      return `Offer Name: ${value}`;
    case "listing__title__icontains":
      return `Offering Name: ${value}`;
    case "status":
      return `Status: ${value}`;
    case "direction":
      return `Type: ${value === 2 || value === "2" ? "Surge" : "Early Bird"}`;
    default:
      return undefined;
  }
};

const formatOfferingName = (record) => {
  let names;
  if (record?.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING) {
    names = record?.listing_uids?.map((l) =>
      typeof l === "string" ? l : l.title
    );
  } else if (record?.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE) {
    names = record?.dynamic_link_uuids?.map((d) =>
      typeof d === "string" ? d : d.title
    );
  }
  if (!names?.length) {
    names = (record?.listing_name || "").split(", ");
  }
  return <OfferingNameChips listingNames={names} />;
};
